import { ActionTree } from "vuex";
import api from "@/api/signup/api";
import {
  IReferenceDataState,
  IReferenceDataTypes,
  IReferenceData,
  IReferenceDataItem,
  IBankDataItem,
  IBanksData
} from "./types";
import { IRootState } from "@/store/types";
import { UI } from "@/store/ui";
import { REFERENCE_DATA } from ".";
import { REFERENCE_DATA_MUTATIONS } from "./mutations";
import { ERROR_MESSAGES } from "@/constants/errors";

export namespace REFERENCE_DATA_ACTIONS {
  export const FETCH_REFERENCE_DATA = "fetchReferenceData";
  export const UPDATE_REFERENCE_DATA = "updateReferenceData";
  export const CREATE_REFERENCE_DATA_ITEM = "createReferenceDataItem";
  export const FETCH_BANK_DATA = "fetchBankData";
}

export const actions: ActionTree<IReferenceDataState, IRootState> = {
  async [REFERENCE_DATA_ACTIONS.FETCH_REFERENCE_DATA]({
    commit,
    dispatch
  }): Promise<void> {
    commit(REFERENCE_DATA.MUTATIONS.TOGGLE_IS_LOADING, true);

    try {
      const response: any = await api.getReferenceData();

      const referenceData: any = response.data.reduce(
        (data: IReferenceDataTypes, item: IReferenceData) => {
          data[item.id] = item;
          return data;
        },
        {}
      );

      commit(REFERENCE_DATA.MUTATIONS.SET_REFERENCE_DATA, referenceData);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-reference-data",
          text: ERROR_MESSAGES.FETCH_REFERENCE_DATA_ERROR,
          error
        },
        { root: true }
      );
    }

    commit(REFERENCE_DATA.MUTATIONS.TOGGLE_IS_LOADING, false);
  },
  async [REFERENCE_DATA_ACTIONS.UPDATE_REFERENCE_DATA](
    { commit, dispatch },
    payload: IReferenceData
  ): Promise<void> {
    commit(REFERENCE_DATA.MUTATIONS.TOGGLE_IS_LOADING, true);

    try {
      await api.updateReferenceData(payload.id, payload);

      // TODO: just update state directly?
      //       for now reload the reference data since its easier.
      await dispatch(REFERENCE_DATA.ACTIONS.FETCH_REFERENCE_DATA);

      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "success-update-reference-data",
          text: "Successfully updated the default value"
        },
        { root: true }
      );
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-update-reference-data",
          text: ERROR_MESSAGES.UPDATE_REFERENCE_DATA_ERROR,
          error
        },
        { root: true }
      );
    }

    commit(REFERENCE_DATA.MUTATIONS.TOGGLE_IS_LOADING, false);
  },
  async [REFERENCE_DATA_ACTIONS.CREATE_REFERENCE_DATA_ITEM](
    { commit, dispatch },
    payload: { referenceDataId: number; item: IReferenceDataItem }
  ): Promise<void> {
    commit(REFERENCE_DATA.MUTATIONS.TOGGLE_IS_LOADING, true);

    try {
      await api.createReferenceDataItem(payload.referenceDataId, payload.item);

      commit(REFERENCE_DATA_MUTATIONS.SET_ADD_NEW_DIALOG_ERROR_MESSAGE, "");

      // reload the reference data list with the newly added item.
      await dispatch(REFERENCE_DATA.ACTIONS.FETCH_REFERENCE_DATA);

      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "create-reference-data-success",
          text: "Successfully created reference data"
        },
        { root: true }
      );
    } catch (error: any) {
      let errorMessage = "Failed to create reference data item";
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.exception
      ) {
        errorMessage = error.response.data.exception;
      }

      commit(
        REFERENCE_DATA_MUTATIONS.SET_ADD_NEW_DIALOG_ERROR_MESSAGE,
        errorMessage
      );
    }

    commit(REFERENCE_DATA.MUTATIONS.TOGGLE_IS_LOADING, false);
  },
  async [REFERENCE_DATA_ACTIONS.FETCH_BANK_DATA]({
    commit,
    dispatch
  }): Promise<void> {
    commit(REFERENCE_DATA.MUTATIONS.TOGGLE_IS_LOADING, true);

    try {
      const response = await api.getBanksData();
      const banksData = response.data.reduce(
        (data: IBanksData, item: IBankDataItem) => {
          data[item.bankId] = item;
          return data;
        },
        {}
      );

      commit(REFERENCE_DATA.MUTATIONS.SET_BANKS_DATA, banksData);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-get-banks-data",
          text: ERROR_MESSAGES.FETCH_BANK_DATA_ERROR,
          error
        },
        { root: true }
      );
    }
    commit(REFERENCE_DATA.MUTATIONS.TOGGLE_IS_LOADING, false);
  }
};
